import type { DemoDomain } from './demoCenter'

export type SolutionId = DemoDomain['id']

export type ImpactBenefit = { title: string; body: string }

export type HoursScenario = {
  kind: 'hours'
  /** The illustrative sentence shown in the panel. */
  text: string
  assumptions: string[]
  measures: string
}

export type GovernmentScenarioRef = { kind: 'government' }

export type SolutionImpact = {
  title: string
  benefits: ImpactBenefit[]
  supporting?: string
  scenario: HoursScenario | GovernmentScenarioRef
}

export type Solution = {
  id: SolutionId
  /** Compact label for navigation, footer and cards. */
  navLabel: string
  /** Solution page / section title. */
  pageTitle: string
  /** Product or offering name shown above the headline. */
  offering: string
  href: string
  headline: string
  intro: string[]
  /** One-line summary for homepage and overview cards. */
  summary: string
  tasks: string[]
  tasksHeading: string
  workspaces: { name: string; role: string }[]
  workflow: string[]
  modules: string[]
  demoLabel: string
  demoCta: string
  impact: SolutionImpact
  /** Development-direction copy, labeled as under development. */
  developmentScope?: string
}

/** Shared copy used across every impact panel. */
export const illustrativeLabel = 'Illustrative scenario'

export const illustrativeExplanation =
  'These examples show what specified improvements would mean. They are planning assumptions, not measured SyNeural results or performance forecasts. Actual results depend on implementation, adoption and service requirements.'

export const demoEntryNote = 'Interactive demo with fictional records. Changes are saved in this browser.'

export const consequentialNote =
  'Methods and integrations are being developed for their intended settings, with authorized review built into the workflow.'

export const homeBenefitLine = 'Less repeated work. More time for people. Better coordinated services.'

/** Seven solutions in canonical order. */
export const solutions: Solution[] = [
  {
    id: 'government',
    navLabel: 'Government',
    pageTitle: 'Government & Public Institutions',
    offering: 'National Capability and Development System',
    href: '/government',
    headline: 'Connected citizen management. Coordinated public services.',
    intro: [
      'SyNeural’s National Capability and Development System brings citizen information, applications, referrals, support plans and institutional performance into a coordinated operating environment. Authorized teams gain a shared view of the work that needs attention, while citizens have their own workspace to request support, track applications and follow their development.',
      'Shared knowledge and workflows help institutions reduce repeated administration, coordinate responsibilities and see where services need improvement—from local delivery to national planning.',
    ],
    summary: 'Citizen records, applications, referrals and program performance in one coordinated operating environment.',
    tasksHeading: 'What it brings together',
    tasks: [
      'Citizen records',
      'Applications and program eligibility',
      'Referrals and services',
      'Support and development plans',
      'Workforce pathways',
      'Credentials',
      'Program performance',
      'Review and administration',
    ],
    workspaces: [
      { name: 'Citizen Workspace', role: 'Request support, track applications and follow personal development.' },
      { name: 'Administration Control Panel', role: 'Manage records, coordinate delivery and review performance across areas.' },
    ],
    workflow: ['Request support', 'Check information', 'Assign the responsible team', 'Coordinate a referral', 'Record the outcome', 'Review progress'],
    modules: ['Services', 'Knowledge', 'Capability', 'Credentials'],
    demoLabel: 'Try the Government workspace',
    demoCta: 'Explore the Government workflow',
    impact: {
      title: 'A more capable government with less administrative overhead',
      benefits: [
        { title: 'Reduce repeated administration', body: 'Reuse information and coordinate applications, referrals and follow-up across teams.' },
        { title: 'Build a leaner operation', body: 'Reduce routine processing work and avoid duplicated staffing as services expand.' },
        { title: 'Put capacity where it matters', body: 'Redirect staff time toward complex cases, local delivery and unresolved needs.' },
      ],
      supporting:
        'A connected operating model creates the opportunity to serve more people without expanding bureaucracy at the same rate. Existing administrations can simplify repeated work; a newly designed administration can avoid building duplicated processes and systems in the first place.',
      scenario: { kind: 'government' },
    },
  },
  {
    id: 'research',
    navLabel: 'Scientific Research',
    pageTitle: 'Scientific Research',
    offering: 'Noetivra Research',
    href: '/solutions#scientific-research',
    headline: 'Connect ideas. Build shared understanding.',
    intro: [
      'Bring papers, concepts, methods and findings into a connected knowledge environment. Noetivra Research helps teams follow findings to their source passages, compare studies and turn reviewed relationships into a reusable knowledge base.',
    ],
    summary: 'Follow findings to their source passages, compare studies and build a reviewed knowledge base.',
    tasksHeading: 'What research teams can do',
    tasks: [
      'Follow a finding back to the exact passage that supports it',
      'Compare studies side by side with their methods and limits',
      'Propose relationships and review them before they become shared knowledge',
      'Keep interpretations, open questions and responsibilities together',
    ],
    workspaces: [
      { name: 'Research Workspace', role: 'Explore sources, compare studies and follow approved relationships.' },
      { name: 'Curation & Administration', role: 'Review proposals against their passages and publish approved knowledge.' },
    ],
    workflow: ['Source', 'Passage', 'Proposed relationship', 'Review', 'Approved knowledge', 'Exploration and comparison'],
    modules: ['Knowledge', 'Collaboration and review'],
    demoLabel: 'Try the Research workspace',
    demoCta: 'Explore the Research workflow',
    impact: {
      title: 'More discovery time, less information handling',
      benefits: [
        { title: 'Find relevant knowledge faster', body: 'Connect concepts and source passages across documents.' },
        { title: 'Reduce duplicated review', body: 'Reuse curated relationships and visible review history.' },
        { title: 'Coordinate research more clearly', body: 'Keep interpretations, questions and responsibilities together.' },
      ],
      scenario: {
        kind: 'hours',
        text: 'If connected retrieval and review saved 20 minutes on each of 1,000 document reviews, a team would release about 333 staff hours for further analysis and research.',
        assumptions: [
          '1,000 reviews in one year × 20 minutes net saved per review ÷ 60 = 333.3 hours.',
          'This is a hypothetical reduction after checking and correction time, not an observed extraction or review speed.',
          'It does not imply that all papers are comparable or that scientific judgment is automated.',
        ],
        measures: 'Time to find usable information, review time per document, correction effort and reuse of reviewed knowledge.',
      },
    },
  },
  {
    id: 'education',
    navLabel: 'Education',
    pageTitle: 'Education & Learning',
    offering: 'Noetivra for Education',
    href: '/education',
    headline: 'Make the next learning step clear.',
    intro: [
      'Connect learning activities, feedback and development plans in one workspace. Learners can understand their progress and act on their next steps, while educators can follow development and refine the support they provide.',
    ],
    summary: 'Connect learning activities, feedback and development plans so every learner knows the next step.',
    tasksHeading: 'What learners and educators can do',
    tasks: [
      'Complete a task with clear criteria and receive focused feedback',
      'Turn feedback into a practical development activity',
      'Compare progress across reassessments in a shared view',
      'Keep tasks, feedback and follow-up in one place',
    ],
    workspaces: [
      { name: 'Learner Workspace', role: 'Complete tasks, read feedback and follow development activities.' },
      { name: 'Educator Workspace', role: 'Review responses, assign practice and follow progress over time.' },
    ],
    workflow: ['Task', 'Response', 'Review', 'Development activity', 'Reassessment'],
    modules: ['Capability', 'Knowledge', 'Progress tracking', 'Credentials (where included in a program)'],
    demoLabel: 'Try the Education workspace',
    demoCta: 'Explore the Education workflow',
    impact: {
      title: 'More time for teaching and individual support',
      benefits: [
        { title: 'Reduce routine coordination', body: 'Keep tasks, feedback and follow-up in one place.' },
        { title: 'Make support more focused', body: 'Connect observed learning needs to practical activities.' },
        { title: 'Follow progress consistently', body: 'Give learners and educators a shared view of the next step.' },
      ],
      scenario: {
        kind: 'hours',
        text: 'Saving 30 minutes of routine administration per educator each week across 100 educators would release 2,000 hours over a 40-week teaching year for instruction, preparation or learner support.',
        assumptions: [
          '100 educators × 0.5 hours per week × 40 weeks = 2,000 hours.',
          'This concerns administrative time, not replacing teachers or shortening necessary assessment.',
          'No attainment improvement is assumed in the arithmetic.',
        ],
        measures: 'Administrative time, feedback turnaround, learner participation and development outcomes.',
      },
    },
  },
  {
    id: 'talent',
    navLabel: 'Workforce',
    pageTitle: 'Employers & Workforce',
    offering: 'Noetivra Talent',
    href: '/employers',
    headline: 'Connect demonstrated strengths to work and opportunity.',
    intro: [
      'Noetivra Talent brings role requirements, candidate review and employee development into a consistent workflow. Teams can compare demonstrated skills, identify unanswered questions and connect development priorities with practical next steps.',
    ],
    summary: 'Compare demonstrated skills against role requirements and connect findings to development.',
    tasksHeading: 'What hiring and development teams can do',
    tasks: [
      'Review work samples against the same job-relevant criteria',
      'Identify unanswered questions before the next stage',
      'Record the hiring decision and its reason',
      'Connect review findings to development and role opportunities',
    ],
    workspaces: [
      { name: 'Candidate / Employee Workspace', role: 'Submit work samples, see recorded outcomes and follow development.' },
      { name: 'Employer Review Workspace', role: 'Compare evidence against role criteria and record decisions.' },
    ],
    workflow: ['Role requirements', 'Work sample', 'Structured review', 'Recorded hiring decision or development plan', 'Follow-up'],
    modules: ['Capability', 'Knowledge', 'Credentials'],
    demoLabel: 'Try the Talent workspace',
    demoCta: 'Explore the Talent workflow',
    developmentScope:
      'Explainable candidate ranking is part of the development direction, comparing job-relevant qualifications, work samples and structured responses against defined criteria. Reviewers can examine the basis for a recommendation and record the hiring decision.',
    impact: {
      title: 'Less processing, clearer hiring and development',
      benefits: [
        { title: 'Streamline candidate preparation', body: 'Organize job-relevant information against consistent requirements.' },
        { title: 'Coordinate workforce development', body: 'Connect review findings to learning and role opportunities.' },
        { title: 'Reduce repeated handling', body: 'Carry reviewed information through the appropriate workflow.' },
      ],
      scenario: {
        kind: 'hours',
        text: 'Saving 15 minutes of administrative preparation per candidate across 4,000 candidate reviews would release 1,000 staff hours per year.',
        assumptions: [
          '4,000 reviews per year × 15 minutes net saved ÷ 60 = 1,000 hours.',
          'The example concerns organizing information, not eliminating interviews or professional judgment.',
          'It does not assume ranking improves hiring quality or that released hours produce equivalent job cuts.',
        ],
        measures: 'Review preparation time, time to resolve missing information, hiring-cycle duration and development follow-through.',
      },
    },
  },
  {
    id: 'leadership',
    navLabel: 'Leadership',
    pageTitle: 'Leadership Development',
    offering: 'Noetivra Leadership',
    href: '/leadership',
    headline: 'Develop judgment for greater responsibility.',
    intro: [
      'Connect decision exercises, coaching and purposeful practice with a visible development journey. Participants and coaches can examine reasoning, trade-offs and follow-through, then track progress across new situations.',
      'Leadership uses the shared Capability module, so the same exercises can run inside government, education and workforce programs.',
    ],
    summary: 'Decision exercises, coaching and practice with a visible development journey.',
    tasksHeading: 'What participants and coaches can do',
    tasks: [
      'Work through a consequential scenario and explain the decision',
      'Review reasoning, trade-offs and follow-through with a coach',
      'Practise with agreed development actions between sessions',
      'Revisit decisions in new situations and compare progress',
    ],
    workspaces: [
      { name: 'Participant Workspace', role: 'Work through scenarios, read feedback and follow agreed practice.' },
      { name: 'Coach Workspace', role: 'Review reasoning, give feedback and compare progress across rounds.' },
    ],
    workflow: ['Scenario', 'Decision and reasoning', 'Feedback', 'Coached practice', 'Reassessment'],
    modules: ['Capability', 'Knowledge', 'Credentials'],
    demoLabel: 'Try the Leadership workspace',
    demoCta: 'Explore the Leadership workflow',
    developmentScope:
      'Portable leadership credentials are being developed to record the scope assessed, issuing organization, methods used and review dates, making achievements easier for participating institutions to understand.',
    impact: {
      title: 'Scale development without scaling administration at the same rate',
      benefits: [
        { title: 'Prepare coaching more efficiently', body: 'Bring scenarios, responses and previous feedback together.' },
        { title: 'Improve continuity', body: 'Make agreed development actions visible between sessions.' },
        { title: 'Make achievements easier to explain', body: 'Connect progress with the scope and methods of a program.' },
      ],
      scenario: {
        kind: 'hours',
        text: 'Saving 15 minutes of preparation and documentation across 2,000 coaching sessions would release 500 hours per year for coaching and program improvement.',
        assumptions: [
          '2,000 sessions per year × 15 minutes net saved ÷ 60 = 500 hours.',
          'This is support-work efficiency, not a claim that leadership quality or readiness increases by a specified percentage.',
        ],
        measures: 'Preparation time, follow-up completion, participant progress and application in role-relevant situations.',
      },
    },
  },
  {
    id: 'community',
    navLabel: 'Community',
    pageTitle: 'Community & Civil Society',
    offering: 'Noetivra for Communities',
    href: '/civil-society',
    headline: 'Connect people with practical support.',
    intro: [
      'Bring mentorship, local knowledge, referrals and program review into a shared workspace. Community teams can coordinate responsibilities, follow agreed support plans and understand what helps participants move forward.',
      'Each organization keeps control of its own records. Referrals share only the information a participant has agreed to.',
    ],
    summary: 'Mentorship, local knowledge and agreed referrals in one shared workspace.',
    tasksHeading: 'What community teams can do',
    tasks: [
      'Agree a goal and support plan with each participant',
      'Make referrals that share only what the participant agreed to',
      'See responsibility, status and missing steps for every referral',
      'Review which programs help participants move forward',
    ],
    workspaces: [
      { name: 'Participant Workspace', role: 'Set goals, follow support plans and see referral status.' },
      { name: 'Program Team Workspace', role: 'Coordinate mentoring, referrals and program review.' },
    ],
    workflow: ['Goal', 'Support plan', 'Mentoring or referral', 'Follow-up', 'Program review'],
    modules: ['Services', 'Knowledge', 'Capability (where appropriate)'],
    demoLabel: 'Try the Community workspace',
    demoCta: 'Explore the Community workflow',
    impact: {
      title: 'Extend support with the resources you have',
      benefits: [
        { title: 'Reduce repeated intake', body: 'Coordinate the information needed for agreed support.' },
        { title: 'Keep referrals moving', body: 'Make responsibility, status and missing steps visible.' },
        { title: 'Focus scarce capacity', body: 'Help teams see which requests need attention.' },
      ],
      scenario: {
        kind: 'hours',
        text: 'Saving 10 minutes of coordination on each of 6,000 referrals would release 1,000 staff hours per year for participant support and program work.',
        assumptions: [
          '6,000 referrals per year × 10 minutes net saved ÷ 60 = 1,000 hours.',
          'The time saving does not assume partner service capacity increases or guarantee more successful referrals.',
        ],
        measures: 'Coordination time, completed referrals, abandoned requests and participant experience.',
      },
    },
  },
  {
    id: 'clinical',
    navLabel: 'Clinical',
    pageTitle: 'Clinical',
    offering: 'Noetivra Clinical',
    href: '/solutions#clinical',
    headline: 'Bring the clinical picture into focus.',
    intro: [
      'Noetivra Clinical brings patient accounts, clinical history, structured assessments and professional observations together with supporting knowledge. Clinicians can review changes over time, identify missing information and document their reasoning in a coherent workspace.',
      'The current demo is a documentation and review workflow. Clinical information stays separate from civic assessment.',
    ],
    summary: 'Patient accounts, history and professional observations together, with clear review over time.',
    tasksHeading: 'What clinical teams can do',
    tasks: [
      'Keep patient reports, measurements, observations and interpretations distinct',
      'Review changes across visits and identify missing information',
      'Document reasoning and send it for professional review',
      'Add later corrections as addenda, preserving reviewed records',
    ],
    workspaces: [
      { name: 'Clinician Workspace', role: 'Gather history, organize observations and document interpretation.' },
      { name: 'Authorized Review Workspace', role: 'Review documentation, request clarification and record outcomes.' },
    ],
    workflow: ['Patient information', 'Visit review', 'Documented interpretation', 'Professional review', 'Follow-up or addendum'],
    modules: ['Knowledge', 'Clinical review'],
    demoLabel: 'Try the Clinical workspace',
    demoCta: 'Explore the Clinical workflow',
    developmentScope:
      'Clinical decision support is part of the development direction, including relevant references, possible diagnostic explanations and treatment options for clinician evaluation.',
    impact: {
      title: 'More time for care, less time assembling the record',
      benefits: [
        { title: 'Bring information together', body: 'Reduce fragmented searching before and after visits.' },
        { title: 'Support continuity', body: 'Make changes, professional observations and follow-up easier to review.' },
        { title: 'Reduce documentation friction', body: 'Organize recorded information and review history clearly.' },
      ],
      scenario: {
        kind: 'hours',
        text: 'Saving 5 minutes of record preparation and documentation per visit across 12,000 visits would release 1,000 clinical staff hours per year for care and review.',
        assumptions: [
          '12,000 visits per year × 5 minutes net saved ÷ 60 = 1,000 hours, after verification and correction effort.',
          'This is not a demonstrated clinical outcome, shorter necessary consultations, improved diagnostic accuracy or a staffing-cut target.',
        ],
        measures: 'Documentation time, correction burden, completeness, clinician workload and patient experience.',
      },
    },
  },
]

export const getSolution = (id: SolutionId) => solutions.find((solution) => solution.id === id) as Solution

/** Government illustrative scenario (USD). */
export const governmentRanges = [
  {
    model: 'Modernize an existing administration',
    positions: '100–300 out of 1,000 positions (10–30%)',
    savings: '$1m–$4m, or 4–16% of a $25m annual operating budget',
  },
  {
    model: 'Design a new administration around shared services',
    positions: '250–450 equivalent positions avoided (25–45%)',
    savings: '$4m–$8m, or 16–32% of the same conventional $25m operating baseline',
  },
]

export const governmentScopeNote =
  'Selected administrative operations at the same workload and service quality—not the entire government workforce. Savings are steady-state illustrations, before initial build and transition costs. A new administration here means a redesigned operating structure, not merely a change of elected government.'

export const governmentBaseline =
  'All values are USD. The baseline is 1,000 administrative positions at $20,000 annual employment cost each, plus $5m in other operating expenses: $25m total. It excludes benefit payments and public-service spending outside the selected operation.'

export const governmentAssumptionColumns = [
  'Existing: cautious',
  'Existing: middle',
  'Existing: ambitious',
  'New: cautious',
  'New: middle',
  'New: ambitious',
]

export const governmentAssumptionRows: { label: string; values: string[]; emphasis?: boolean }[] = [
  { label: 'Positions reduced / avoided', values: ['100', '200', '300', '250', '350', '450'] },
  { label: 'Gross employment-cost savings / avoidance', values: ['$2m', '$4m', '$6m', '$5m', '$7m', '$9m'] },
  { label: 'Other operating costs avoided', values: ['$0', '$0', '$0', '$1m', '$1.5m', '$2m'] },
  { label: 'Additional annual platform, operations and assurance costs', values: ['$1m', '$1.5m', '$2m', '$2m', '$2.5m', '$3m'] },
  { label: 'Net annual savings / cost avoidance', values: ['$1m', '$2.5m', '$4m', '$4m', '$6m', '$8m'], emphasis: true },
  { label: 'Percentage of baseline operating budget', values: ['4%', '10%', '16%', '16%', '24%', '32%'] },
]

export const governmentFormula =
  'Net recurring savings = employment costs removed or avoided + other operating costs avoided − additional recurring platform/operations costs.'

export const governmentCaveats = [
  'Payroll savings occur only when positions actually leave the budget or are avoided. Redeploying existing employees provides additional capacity rather than equivalent cash savings, and fragmented time savings are not the same as a headcount reduction.',
  'The staffing figures are assumed scenario inputs, not conclusions derived from a tested automation rate.',
  'Initial build, migration, training, severance where applicable and transition costs are excluded. These selected-operation percentages should not be projected onto a whole national budget.',
]
